import { useCallback, useMemo } from 'react';
import { useGlobalStore } from '../../stores/globalStore';
import { useReportList, useReportFavorites } from '../../api/reportsApi';
import type { Report } from '../../types/reports';
import { STATIC_REPORTS } from './staticReports';
import { findReportConfig } from './reportConfigs';
import {
  CATALOG_GROUPS,
  getCatalogEntry,
  isReportUnavailable,
  isReportHiddenForFacility,
  isReportHiddenForUser,
} from './reportCatalog';
import type { CatalogGroupKey } from './reportCatalog';
import { favoritesForCard, type LegacyName } from './reportFavoriteKeys';
import { RETIRED_REPORT_PKEYS } from './retiredReports';
import type { SearchableReport } from './reportSearch';
import { usePermissionEnforcement } from '../../hooks/usePermissionEnforcement';
import { isReportHiddenByRole } from './reportVisibility';
import { fillUnresolvedTokens } from '../../utils/reportTokens';

/**
 * The report catalog the user may open, and which of it they starred: one source for the Reports
 * page (AllReportsNext) and the sidebar's Favorites group under Reports, so starring or unstarring
 * on the page shows in the panel at once and the two never disagree. The queries are the page's
 * (useReportList, useReportFavorites: same react-query keys, same cache), and every rule that
 * keeps a card off the page (unavailable, facility, the desktop's role rules, Plan of Care level)
 * keeps it out of the favorites too.
 */

/**
 * Server-side report pKeys that must not become their own card because
 * a single card already owns them.
 *
 * Management Team - Chargeable submits to pKey 30, 29 or 28 depending
 * on the Report Type radio; the catalog shows one card at pKey 30 and
 * `config.resolveFkeyReport` picks the right sibling at submit time.
 * The API still returns rows for the siblings, so they are suppressed
 * here. This is the only pKey-keyed list left in the page, and it
 * encodes card identity rather than classification.
 */
const FOLDED_SIBLING_PKEYS: ReadonlySet<number> = new Set([
  28, 29, // Management Team - Chargeable  -> pKey 30
  31, 32, // Management Team - Fiber       -> pKey 33
  34, 35, // Management Team - Protein     -> pKey 36
  37, 38, // Management Team - Thickener   -> pKey 39
  40, 41, // Management Team - Special Req -> pKey 42
  5, 6,   // Nourishment Summary / List    -> pKey 7
  45,     // Resident Multiples Cardex     -> pKey 44
]);

/**
 * Replace `{FieldName}` placeholders from facility settings.
 * Mirrors WinForms `FacilitySettings.PopulateStringWithSettingValues()`.
 */
function replacePlaceholders(
  str: string,
  settings: Record<string, unknown> | null,
): string {
  if (!str || !settings) return str;
  const ciMap: Record<string, string> = {};
  for (const key of Object.keys(settings)) ciMap[key.toLowerCase()] = key;
  return str.replace(/\{(\w+)\}/g, (match, field: string) => {
    const realKey = ciMap[field.toLowerCase()];
    const val = realKey != null ? settings[realKey] : undefined;
    return val != null ? String(val) : match;
  });
}

/**
 * Drop a redundant family prefix/suffix from a card title.
 * "Beverage - By Cold/Hot" under family "Beverage" -> "By Cold/Hot".
 *
 * The old version inferred the tag to strip from a chain of guesses.
 * Now `family` is declared explicitly in the catalog, so this is a
 * plain string operation with no inference to get wrong.
 *
 * The prefix comparison is deliberately loose in one direction only:
 * the leading segment and the family must be prefixes of each other
 * once whitespace is squashed. That is what lets "Posting - Daily Menu"
 * strip under family "Posting Menu", and "Recipe - List" under
 * "Recipes", without matching two unrelated words. When even that is
 * wrong for a given report, the catalog sets an explicit `title`.
 */
export function stripFamily(name: string, family: string | undefined): string {
  if (!name || !family || name === family) return name;
  const squash = (s: string) => s.replace(/\s/g, '').toLowerCase();
  const fam = squash(family);
  const dash = name.indexOf(' - ');
  if (dash > 0) {
    const head = squash(name.slice(0, dash));
    if (head && (head.startsWith(fam) || fam.startsWith(head))) {
      return name.slice(dash + 3);
    }
  }
  const suffix = ` - ${family}`;
  if (name.toLowerCase().endsWith(suffix.toLowerCase())) {
    return name.slice(0, name.length - suffix.length);
  }
  return name;
}

/** A catalog row: the API report joined to its catalog entry. */
export interface CatalogRow extends SearchableReport {
  group: CatalogGroupKey;
  /** Title with the family prefix stripped, for the card. */
  cardTitle: string;
}

export function useReportCatalog() {
  const facilityId = useGlobalStore((s) => s.facilityId);
  const userId = useGlobalStore((s) => s.userId);
  const userRole = useGlobalStore((s) => s.userRole);
  // The desktop's per-report rules (reportVisibility.ts) hide cards only on an enforced connection.
  const { enforced: permissionsEnforced, settled: permissionsSettled } = usePermissionEnforcement();
  const facilitySettings = useGlobalStore((s) => s.facilitySettings);
  const facilitySettingsLoaded = useGlobalStore((s) => s.facilitySettingsLoaded);
  const residentLabel = useGlobalStore((s) => s.residentLabel);
  const creditsLabel = useGlobalStore((s) => s.creditsLabel);
  const facilityLabel = useGlobalStore((s) => s.facilityLabel);
  const specialDeviceLabel = useGlobalStore((s) => s.specialDeviceLabel);
  const prescriberLabel = useGlobalStore((s) => s.prescriberLabel);
  const ehrTitleLabel = useGlobalStore((s) => s.ehrTitleLabel);
  const setNameLabel = useGlobalStore((s) => s.setNameLabel);
  const roomLabel = useGlobalStore((s) => s.roomLabel);
  const residentPluralLabel = useGlobalStore((s) => s.residentPluralLabel);

  /**
   * Resolve facility-scoped tokens. Everything the user can see — and
   * therefore everything search runs over — passes through here, so a
   * facility that renames "Prescriber" to "Doctor" can find that card
   * by typing "doctor". Search used to run against the raw token.
   */
  const resolveLabel = useCallback((s: string | null | undefined): string => {
    if (!s) return '';
    return s
      .replace(/\{Credits\}/g, creditsLabel)
      .replace(/\{SpecialDevice\}/g, specialDeviceLabel)
      .replace(/\{Prescriber\}/g, prescriberLabel)
      .replace(/\{EHR_Title\}/g, ehrTitleLabel)
      .replace(/\{Facility\}/g, facilityLabel)
      .replace(/\{Resident\}/g, residentLabel)
      // The facility's own words for sets and rooms ("By {SetName}" on Beverage's card).
      .replace(/\{SetName_Plural\}/g, `${setNameLabel}s`)
      .replace(/\{SetName\}/g, setNameLabel)
      .replace(/\{Room\}/g, roomLabel)
      .replace(/\{Resident_?Plural\}/g, residentPluralLabel)
      // Anything left gets the desktop's default word, never its braces.
      .replace(/\{\w+\}/g, (m) => fillUnresolvedTokens(m));
  }, [creditsLabel, specialDeviceLabel, prescriberLabel, ehrTitleLabel, facilityLabel, residentLabel, setNameLabel, roomLabel, residentPluralLabel]);

  const { data: apiReports, isLoading: reportsLoading, isError: reportsFailed } = useReportList(facilityId);
  const { data: favorites, isLoading: favoritesLoading, isError: favoritesFailed } = useReportFavorites(facilityId, userId);
  /**
   * The favorites list is final: both lists have answered (a query that has not started yet, while
   * the facility or user is still 0, is not "loading" to react-query but has no answer either), and
   * so have the facility's settings and the permission mode, each of which can add or drop a
   * favorite. Until then the list can still change, so the sidebar waits instead of drawing it
   * several times over (Jeff, 2026-10-26: "very bouncy loading the favorites").
   */
  const favoritesAnswered = (apiReports !== undefined || reportsFailed)
    && (favorites !== undefined || favoritesFailed);
  const favoritesSettled = favoritesAnswered && facilitySettingsLoaded && permissionsSettled;

  /**
   * The catalog rows. One pass: resolve the config, look up the
   * catalog entry, resolve tokens. No inference, no re-derivation.
   */
  const rows = useMemo<CatalogRow[]>(() => {
    const settings = facilitySettings as unknown as Record<string, unknown>;

    // Static entries exist for cards whose server pKey has no DB row —
    // sibling cards that share a generator via `resolveFkeyReport`
    // (e.g. Tube Feed - List at synthetic pKey 6900 submits under 69).
    const apiPKeys = new Set((apiReports ?? []).map((r) => r.pKey_RS_Report));
    const orphanStatics = STATIC_REPORTS.filter(
      (s) => !apiPKeys.has(s.pKey_RS_Report) && findReportConfig(s.slug) != null,
    );
    const raw: Report[] = apiReports && apiReports.length > 0
      ? [...apiReports, ...orphanStatics]
      : STATIC_REPORTS;

    const out: CatalogRow[] = [];
    const seenSlugs = new Set<string>();

    for (const r of raw) {
      if (FOLDED_SIBLING_PKEYS.has(r.pKey_RS_Report)) continue;
      // Dropped cards (89, 133, 135); their replacements have their own rows.
      if (RETIRED_REPORT_PKEYS.has(r.pKey_RS_Report)) continue;

      const config = findReportConfig(r.slug, r.programName, r.reportName, r.pKey_RS_Report);
      const slug = config?.slug ?? r.slug;

      // Server generator missing, commented out, WinForms-only, or the
      // report is internal-only. Reason lives in UNAVAILABLE_REPORTS.
      if (isReportUnavailable(slug)) continue;
      // EHR Integration reports only when the facility's EHR is active.
      if (isReportHiddenForFacility(slug, facilitySettings)) continue;
      // The desktop report tree's role and facility rules (staged: only when enforced).
      if (isReportHiddenByRole(slug, permissionsEnforced, facilitySettings, userRole)) continue;
      // Plan of Care reports need the Plan of Care report level (the desktop's menu), staged the same way.
      if (isReportHiddenForUser(slug, permissionsEnforced, userRole)) continue;

      // The API can return several rows resolving to one card.
      if (seenSlugs.has(slug)) continue;
      seenSlugs.add(slug);

      const entry = getCatalogEntry(slug);

      // Config names are canonical — we control them; the DB's drift.
      const rawName = config?.reportName || r.reportName || slug;
      const name = resolveLabel(replacePlaceholders(rawName, settings));
      const family = entry.family ? resolveLabel(entry.family) : undefined;
      const groupLabel = CATALOG_GROUPS[entry.group].label;

      out.push({
        slug,
        pKey: r.pKey_RS_Report,
        // `name` stays the full underlying name so search still matches
        // "production" for a card titled "Grocery List".
        name,
        cardTitle: entry.title
          ? resolveLabel(entry.title)
          : stripFamily(name, family),
        groupLabel,
        family,
        description: resolveLabel(entry.description),
        keywords: entry.keywords ?? '',
        group: entry.group,
      });
    }

    return out;
  }, [apiReports, facilitySettings, resolveLabel, permissionsEnforced, userRole]);

  /**
   * Favorites are keyed the way the desktop keys them (its reports-tree
   * node), not by pKey_RS_Report - see reportFavoriteKeys.ts. `apiNames`
   * lets favorites the website saved before that (under the card name or,
   * from the classic page, the DB report name) be recognised.
   */
  const useProduction = facilitySettings?.useProduction;
  const apiNames = useMemo<LegacyName[]>(
    () => [
      // The names the cards show (what this page saved, tokens resolved).
      ...rows.map((r) => ({ rsPKey: r.pKey, slug: r.slug, name: r.name })),
      ...(apiReports ?? []).map((r) => ({
        rsPKey: r.pKey_RS_Report,
        slug: findReportConfig(r.slug, r.programName, r.reportName, r.pKey_RS_Report)?.slug ?? r.slug,
        name: r.reportName,
      })),
    ],
    [rows, apiReports],
  );
  const favoriteSlugs = useMemo(
    () => new Set(rows
      .filter((r) => favoritesForCard(favorites, r.slug, r.pKey, useProduction, apiNames).length > 0)
      .map((r) => r.slug)),
    [rows, favorites, useProduction, apiNames],
  );

  const favouriteRows = useMemo(
    () => rows.filter((r) => favoriteSlugs.has(r.slug))
      .sort((a, b) => a.cardTitle.localeCompare(b.cardTitle)),
    [rows, favoriteSlugs],
  );

  return {
    facilityId, userId, rows, apiReports, reportsLoading, favorites, favoritesLoading, favoritesAnswered, favoritesSettled,
    useProduction, apiNames, favoriteSlugs, favouriteRows,
  };
}
